import {
  personsInEntityKeys,
  queryClient,
  deleteKeyPersonFromBusinessEntity,
} from '@commenda/api';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FeaturedIcon,
  Icons,
  LoadingIndicator,
  useToggle,
} from '@commenda/kakashi';
import { useNotification } from '../../../hooks/useNotification';

export const DeleteKeyPersonModal = ({
  isOpen,
  onClose,
  personId,
  businessEntityId,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  personId: number | null;
  businessEntityId: number;
  onSuccess?: () => void;
}) => {
  const { setNotification } = useNotification();
  const isDeletingKeyPerson = useToggle();

  const handleDelete = async () => {
    if (!personId) return;

    isDeletingKeyPerson.on();
    try {
      const response = await deleteKeyPersonFromBusinessEntity(
        businessEntityId,
        personId
      );

      if (response.ok) {
        setNotification({
          show: true,
          title: 'Key person deleted',
          message: 'The key person has been successfully removed.',
          valid: true,
        });

        queryClient.invalidateQueries({
          queryKey: [
            personsInEntityKeys.FETCH_PERSONS_IN_ENTITY,
            { businessEntityId },
          ],
        });

        onSuccess?.();
        onClose();
      } else {
        const errorData = await response.json();
        setNotification({
          show: true,
          title: 'Failed to delete key person',
          message: errorData.message || 'Please try again.',
          valid: false,
        });
      }
    } catch (error) {
      console.error('Error deleting key person:', error);
      setNotification({
        show: true,
        title: 'Failed to delete key person',
        message: 'An error occurred. Please try again.',
        valid: false,
      });
    } finally {
      isDeletingKeyPerson.off();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[400px]">
        <DialogHeader className="flex flex-col gap-xl items-start border-b-0">
          <FeaturedIcon
            icon={<Icons.AlertTriangle />}
            size="lg"
            variant="warning"
          />
          <div className="flex flex-col gap-xs">
            <DialogTitle>Delete key person?</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this key person from the entity?
              This action cannot be undone.
            </DialogDescription>
          </div>
        </DialogHeader>
        <DialogFooter className="border-t-0">
          <Button
            variant="secondary"
            onClick={onClose}
            size="sm"
            disabled={isDeletingKeyPerson.visible}
          >
            Cancel
          </Button>
          <Button
            variant="destructive-primary"
            onClick={handleDelete}
            size="sm"
            disabled={isDeletingKeyPerson.visible}
          >
            {isDeletingKeyPerson.visible ? (
              <LoadingIndicator size={16} />
            ) : null}
            <span>Delete</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
